/**
 * Worker Manager
 * Handles creation and initialization of analysis worker
 * Based on AnalysisProcessor.vue preloadWorkerOpenCV logic
 */

import { MESSAGE_TYPES } from '@/core/analysis/modules/constants'

const WORKER_DEBUG_PREFIX = '[PSDBG][worker-manager]';
const WORKER_PRELOAD_TIMEOUT_MS = 90000;

/**
 * Creates and initializes a new worker instance
 * @param {Object} options
 * @param {string} options.purpose Identifies the worker lifecycle owner for diagnostics
 * @returns {Promise<Worker>} Resolves with ready worker instance
 */
export async function createWorker(options = {}) {
    return new Promise((resolve, reject) => {
        const AnalysisWorker = require('worker-loader!@/core/analysis/analysisWorker.js');
        const preloadStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const purpose = options.purpose || 'analysis-runtime';

        console.log(`${WORKER_DEBUG_PREFIX} createWorker requested`, {
            purpose,
            href: window.location?.href,
            origin: window.location?.origin
        });
        const worker = new AnalysisWorker.default();

        // Set up message handler for preload confirmation
        let timeoutId;
        const cleanup = () => {
            worker.removeEventListener('message', handlePreload);
            worker.removeEventListener('message', handleErrorMessage);
            worker.removeEventListener('error', handleWorkerError);
            worker.removeEventListener('messageerror', handleWorkerError);
            clearTimeout(timeoutId);
        };
        const handlePreload = (event) => {
            if (event.data.type === MESSAGE_TYPES.OPENCV_READY) {
                const elapsedMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - preloadStartedAt;
                console.log(`${WORKER_DEBUG_PREFIX} preload ready`);
                console.log(`${WORKER_DEBUG_PREFIX} preload summary`, {
                    purpose,
                    elapsedMs: Math.round(elapsedMs)
                });
                cleanup();
                resolve(worker);
            }
        };
        const handleErrorMessage = (event) => {
            // 只监听 opencv_error：analysisWorker 在预加载失败时只会发送 opencv_error，
            // 历史上监听的 preload_error 从未被发送（死分支），已移除。
            if (event.data?.type === MESSAGE_TYPES.OPENCV_ERROR) {
                const message = event.data?.error || 'Worker preload failed';
                console.error(`${WORKER_DEBUG_PREFIX} preload error message`, { purpose, message });
                cleanup();
                worker.terminate();
                reject(new Error(message));
            }
        };
        const handleWorkerError = (event) => {
            const message = event?.message || 'Worker error during preload';
            console.error(`${WORKER_DEBUG_PREFIX} worker runtime error`, {
                purpose,
                message,
                filename: event?.filename,
                lineno: event?.lineno,
                colno: event?.colno
            });
            console.error(`${WORKER_DEBUG_PREFIX} worker preload runtime error`, { purpose, message });
            cleanup();
            worker.terminate();
            reject(new Error(message));
        };

        worker.addEventListener('message', handlePreload);
        worker.addEventListener('message', handleErrorMessage);
        worker.addEventListener('error', handleWorkerError);
        worker.addEventListener('messageerror', handleWorkerError);

        // Send preload message to initialize OpenCV and TensorFlow
        console.log(`${WORKER_DEBUG_PREFIX} postMessage preload`, { purpose });
        worker.postMessage({ type: MESSAGE_TYPES.PRELOAD });

        // Timeout as fallback：超时即清理监听并 terminate 无所有者 Worker（§5.3）。
        // 此时该实例尚未交给 Store 或组件，若不 terminate 会成为所有权悬空的孤儿 Worker；
        // 迟到 OPENCV_READY 已因 cleanup() 移除监听而无副作用，无需再处理。
        timeoutId = setTimeout(() => {
            console.error(`${WORKER_DEBUG_PREFIX} preload timeout after ${WORKER_PRELOAD_TIMEOUT_MS}ms; terminating orphan worker`);
            const elapsedMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - preloadStartedAt;
            console.error(`${WORKER_DEBUG_PREFIX} preload summary`, {
                purpose,
                elapsedMs: Math.round(elapsedMs),
                timedOut: true
            });
            cleanup();
            worker.terminate();
            reject(new Error('Worker preload timeout'));
        }, WORKER_PRELOAD_TIMEOUT_MS);
    });
}
